import React, { useState, useEffect, useRef } from 'react';
import SelectField from '../common/SelectField.js';
import { useParams, useNavigate } from 'react-router-dom';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import attendanceService from '../../services/AttendanceService.js';
import userService from '../../services/UserService.js';
import axiosInstance from '../../config/axios.js';
import { Calendar, Clock, MapPin, TrendingUp, ArrowLeft } from 'lucide-react';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';


const ViewAttendanceReport = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [userId, selectedMonth, selectedYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [userRes, reportRes] = await Promise.all([
        userService.getById(userId),
        attendanceService.getUserMonthlyReport(userId, selectedYear, selectedMonth)
      ]);

      setUser(userRes.data);
      setMonthlyReport(reportRes.data);
    } catch (err) {
      Alert.error('Failed to load attendance report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const handleDelete = async (attendanceId, attendanceDate) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete the attendance record for ${formatDate(attendanceDate)}? This action cannot be undone.`,
      'Delete Attendance Record'
    );
    
    if (confirmed) {
      try {
        await axiosInstance.delete(`/attendance/${attendanceId}`);
        Alert.success('Attendance record deleted successfully!');
        fetchData(); // Refresh the report
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete attendance record');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getAttendancePercentage = () => {
    if (!monthlyReport) return 0;
    return ((monthlyReport.days_worked / monthlyReport.total_days_in_month) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading attendance report..." />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => navigate('/attendance')}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-buttonBg" />
                        Attendance Report
                      </h1>
                      <p className="text-sm text-gray-600">{user?.name}</p>
                    </div>
                  </div>

                  {/* Month/Year Selector */}
                  <div className="flex gap-2">
                    <SelectField variant="form"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                    >
                      {monthNames.map((month, index) => (
                        <option key={index + 1} value={index + 1}>
                          {month}
                        </option>
                      ))}
                    </SelectField>
                    <input
                      type="number"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      min="2020"
                      max="2099"
                      className="w-24 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                    />
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-6">
                  {/* Total Days */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Total Days</p>
                        <p className="text-2xl font-bold text-gray-900">{monthlyReport?.total_days_in_month}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  {/* Days Worked */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Days Worked</p>
                        <p className="text-2xl font-bold text-green-600">{monthlyReport?.days_worked}</p>
                      </div>
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </div>

                  {/* Days Absent */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Days Absent</p>
                        <p className="text-2xl font-bold text-red-600">{monthlyReport?.days_absent}</p>
                      </div>
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-red-600" />
                      </div>
                    </div>
                  </div>

                  {/* Total Hours */}
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Total Hours</p>
                        <p className="text-2xl font-bold text-orange-600">{monthlyReport?.total_work_hours_formatted}</p>
                      </div>
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <Clock className="w-6 h-6 text-orange-600" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attendance Percentage */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-sm mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm mb-1">Attendance Rate</p>
                      <p className="text-4xl font-bold">{getAttendancePercentage()}%</p>
                    </div>
                    <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <span className="text-3xl">📊</span>
                    </div>
                  </div>
                  <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2">
                    <div
                      className="bg-white h-2 rounded-full transition-all duration-500"
                      style={{ width: `${getAttendancePercentage()}%` }}
                    ></div>
                  </div>
                </div>

                {/* Detailed Records */}
                <div className="bg-white rounded-lg shadow-sm">
                  <div className="p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Daily Attendance Records</h2>
                  </div>

                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">DATE</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">LOGIN TIME</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">LOGOUT TIME</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">WORK HOURS</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">LOGIN LOCATION</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">LOGOUT LOCATION</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">STATUS</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {monthlyReport?.attendance_records?.length > 0 ? (
                          monthlyReport.attendance_records.map((record) => (
                            <tr key={record.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{formatDate(record.attendance_date)}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{formatTime(record.login_time)}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {record.is_logged_out ? formatTime(record.logout_time) : (
                                  <span className="text-green-600 font-medium">Active</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 font-medium">{record.work_hours_formatted}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {record.login_location ? (
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3 text-gray-400" />
                                    <span>{record.login_location.city}, {record.login_location.state}</span>
                                  </div>
                                ) : 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {record.logout_location ? (
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3 text-gray-400" />
                                    <span>{record.logout_location.city}, {record.logout_location.state}</span>
                                  </div>
                                ) : 'N/A'}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  record.is_logged_out 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {record.is_logged_out ? 'Completed' : 'Active'}
                                </span>
                              </td>
                              <td className="px-4 py-3 relative">
                                <RowActionsMenu
                                  isOpen={activeDropdown === record.id}
                                  onToggle={() => toggleDropdown(record.id)}
                                  onClose={() => setActiveDropdown(null)}
                                  menuRef={dropdownRef}
                                  onView={() => navigate(`/attendance/view/${record.id}`)}
                                  onEdit={() => navigate(`/attendance/edit/${record.id}`)}
                                  onDelete={() => handleDelete(record.id, record.attendance_date)}
                                />
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                              No attendance records for this month
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden p-4 space-y-4">
                    {monthlyReport?.attendance_records?.length > 0 ? (
                      monthlyReport.attendance_records.map((record) => (
                        <div key={record.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start mb-3 gap-2">
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900">{formatDate(record.attendance_date)}</p>
                              <p className="text-sm text-gray-600">{record.work_hours_formatted}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                                record.is_logged_out
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {record.is_logged_out ? 'Completed' : 'Active'}
                              </span>
                              <RowActionsMenu
                                isOpen={activeDropdown === record.id}
                                onToggle={() => toggleDropdown(record.id)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/attendance/view/${record.id}`)}
                                onEdit={() => navigate(`/attendance/edit/${record.id}`)}
                                onDelete={() => handleDelete(record.id, record.attendance_date)}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                            <div>
                              <span className="text-gray-500">Login:</span>
                              <p className="text-gray-900 font-medium">{formatTime(record.login_time)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Logout:</span>
                              <p className="text-gray-900 font-medium">
                                {record.is_logged_out ? formatTime(record.logout_time) : 'Active'}
                              </p>
                            </div>
                            {record.login_location && (
                              <div className="col-span-2 pt-2 border-t border-gray-200">
                                <p className="text-xs text-gray-500 mb-1">Location</p>
                                <p className="text-sm text-gray-700">
                                  {record.login_location.city}, {record.login_location.state}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No attendance records for this month
                      </div>
                    )}
                  </div>
                </div>
              </div>
    </DashboardLayout>
  );
};

export default ViewAttendanceReport;